import * as yup from "yup";

// URL validation reusing the same regex used elsewhere in the portal
// (src/components/Brief/constants.ts:websiteRegExp). Inlined here so the
// PdpAudit module is self-contained when reused in the public iframe.
const websiteRegExp =
    /((https?:\/\/)?(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;

// Schema is parameterized by `requireEmail` so we can drop the email field
// for logged-in users without forking the whole form. Marketing consent is
// gated to the same `requireEmail` flag — it's a required opt-in for
// anonymous lead capture, omitted entirely for logged-in users.
export const buildPdpAuditFormSchema = (requireEmail: boolean) =>
    yup.object().shape({
        url: yup
            .string()
            .trim()
            .required("Enter the URL of your product detail page")
            .matches(websiteRegExp, {
                message:
                    "Please enter a valid URL (e.g. https://example.com/product)",
                excludeEmptyString: true,
            }),
        email: requireEmail
            ? yup
                  .string()
                  .trim()
                  .required("Enter your email to receive the report")
                  .email("Please enter a valid email address")
            : yup.string().trim().notRequired(),
        marketingConsent: requireEmail
            ? yup
                  .boolean()
                  .oneOf([true], "You must agree to receive emails to view the report")
                  .required()
            : yup.boolean().notRequired(),
    });

// Backwards-compat default (anonymous flow). Keep around so existing
// imports don't break.
export const pdpAuditFormSchema = buildPdpAuditFormSchema(true);

export interface PdpAuditFormValues {
    url: string;
    email: string;
    marketingConsent: boolean;
}

export const pdpAuditInitialValues: PdpAuditFormValues = {
    url: "",
    email: "",
    marketingConsent: false,
};

// Client-side sanity check for product-page URL inputs; backends re-validate.
export function isValidUrl(raw: string): boolean {
    const value = normalizeUrl(raw);
    if (!value) return false;
    try {
        const parsed = new URL(value);
        return (
            (parsed.protocol === "http:" || parsed.protocol === "https:") &&
            parsed.hostname.includes(".")
        );
    } catch {
        return false;
    }
}

// Normalize a URL so a missing scheme doesn't fail server-side fetch.
export function normalizeUrl(input: string): string {
    const trimmed = (input || "").trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}
